import * as React from 'react';
import { useCallback } from 'react';
import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDrawerContext } from '../../contexts';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { Collapse, Icon, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { applicationInfo, environment } from '../../environment';
import { LayoutMain } from '../../layouts';

const env = environment();

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: theme.spacing(0, 1),
    height: 80,
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        ...(open && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': openedMixin(theme),
        }),
        ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': closedMixin(theme),
        }),
    }),
);

interface IMainDrawerProps {
    children: React.ReactNode;
    title: string;
}

export const MainDrawer: React.FC<IMainDrawerProps> = ({ children, title }) => {
    const theme = useTheme();
    const smDown = useMediaQuery(theme.breakpoints.down('sm'));
    const { isDrawerOpen, drawerOptions } = useDrawerContext();
    const [collapsed, setCollapsed] = React.useState<string | false>(false);
    const navigate = useNavigate();
    const location = useLocation();
    const logo = theme.palette.mode === 'dark' ? env.logoLight : env.logo;

    const handleNavigate = useCallback((path: string) => {
        path ? navigate(path) : undefined;
    }, [navigate]);

    const handleClick = (label: string) => {
        setCollapsed((prev) => prev === label ? false : label);
    };

    React.useEffect(() => {
        const activeParent = drawerOptions.find((drawerOption) =>
            drawerOption.childrens?.some((child) => child.path === location.pathname)
        );

        if (activeParent) {
            setCollapsed(activeParent.label);
        }
    }, [drawerOptions, location.pathname]);

    return (
        <Box display='flex' gap={2}>
            <CssBaseline />
            <Drawer
                variant='permanent'
                open={smDown ? !isDrawerOpen : isDrawerOpen}
                sx={{
                    '& .MuiPaper-root': {
                        margin: theme.spacing(2),
                        height: '96.7%',
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 6px 18px rgba(16, 24, 40, 0.06)'
                    },
                }}>
                <DrawerHeader>
                    <Box
                        width='100%'
                        display='flex'
                        flexDirection={isDrawerOpen ? 'column' : 'row'}
                        alignContent={isDrawerOpen ? 'flex-start' : 'center'}
                        alignItems={isDrawerOpen ? 'flex-start' : 'center'}
                        justifyContent='center'
                        gap={0.5}
                        p={0.6}
                        overflow='hidden'
                    >
                        <Box
                            component='img'
                            src={logo}
                            alt='MTP'
                            sx={{
                                display: 'block',
                                width: isDrawerOpen ? 118 : 34,
                                height: isDrawerOpen ? 52 : 34,
                                objectFit: 'contain',
                                objectPosition: 'left center',
                                flexShrink: 0,
                            }}
                        />
                        <Typography
                            color='text.secondary'
                            noWrap
                            variant='caption'
                            sx={{
                                display: isDrawerOpen ? 'block' : 'none',
                                maxWidth: '100%',
                                fontWeight: 700,
                                lineHeight: 1.2,
                                pl: 0.2,
                            }}
                        >
                            {applicationInfo.applicatioName}
                        </Typography>
                    </Box>
                </DrawerHeader>
                <Divider variant='middle' />
                <List component='nav'>
                    {drawerOptions.map((drawerOption, index) => (
                        <React.Fragment key={`${index}`}>
                            {drawerOption.label === 'divider' ? (
                                <Divider variant='middle' />
                            ) : (
                                <Tooltip title={isDrawerOpen ? '': drawerOption.label} placement={'bottom-start'}>
                                    <ListItem
                                        key={drawerOption.label}
                                        disablePadding
                                        onClick={() => handleClick(drawerOption.label)}
                                        sx={{
                                            display: 'block',
                                        }}
                                    >
                                        <ListItemButton
                                            selected={collapsed === drawerOption.label}
                                            sx={{
                                                justifyContent: isDrawerOpen ? 'initial' : 'center',
                                                px: 1.5,
                                                borderRadius: 2,
                                                mx: 1,
                                                mb: .5,
                                                color: 'text.primary',
                                                '&.Mui-selected': {
                                                    bgcolor: 'rgba(91, 178, 221, 0.14)',
                                                    color: 'text.primary',
                                                },
                                                '&.Mui-selected:hover': {
                                                    bgcolor: 'rgba(91, 178, 221, 0.2)',
                                                },
                                            }}
                                        >
                                            <ListItemIcon
                                                sx={{
                                                    minWidth: 0,
                                                    mr: isDrawerOpen ? 1 : 'auto',
                                                    justifyContent: 'center',
                                                    color: 'inherit',
                                                }}
                                            >
                                                <Icon fontSize='small' color={drawerOption.IconColor ? drawerOption.IconColor : undefined}>
                                                    {drawerOption.icon}
                                                </Icon>
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={drawerOption.label}
                                                sx={{ opacity: smDown ? isDrawerOpen ? 0 : 1 : isDrawerOpen ? 1 : 0 }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                </Tooltip>
                            )}
                            <Collapse in={collapsed === drawerOption.label} timeout="auto" unmountOnExit>
                                {drawerOption.childrens?.map((child, index) => (
                                    <React.Fragment key={`${index}`}>
                                        {child.label === 'divider' ? (
                                            <Divider variant='middle' />
                                        ) : (
                                            <Tooltip title={isDrawerOpen ? '': child.label} placement={'bottom-start'}>
                                                <ListItem
                                                    key={child.label}
                                                    disablePadding
                                                    onClick={() => handleNavigate(child.path)}
                                                    sx={{
                                                        display: 'block',
                                                    }}
                                                >
                                                    <ListItemButton
                                                        selected={child.path === location.pathname}
                                                        sx={{
                                                            justifyContent: isDrawerOpen ? 'initial' : 'center',
                                                            px: 1.5,
                                                            borderRadius: 2,
                                                            mx: 1,
                                                            mb: .5,
                                                            ml: 4,
                                                            color: 'text.primary',
                                                            '&.Mui-selected': {
                                                                bgcolor: 'rgba(91, 178, 221, 0.14)',
                                                                color: 'text.primary',
                                                            },
                                                            '&.Mui-selected:hover': {
                                                                bgcolor: 'rgba(91, 178, 221, 0.2)',
                                                            },
                                                        }}
                                                    >
                                                        <ListItemIcon
                                                            sx={{
                                                                minWidth: 0,
                                                                mr: isDrawerOpen ? 1 : 'auto',
                                                                justifyContent: 'center',
                                                                color: 'inherit'
                                                            }}
                                                        >
                                                            <Icon fontSize='small'>{child.icon}</Icon>
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={child.label}
                                                            sx={{ opacity: smDown ? isDrawerOpen ? 0 : 1 : isDrawerOpen ? 1 : 0 }}
                                                        />
                                                    </ListItemButton>
                                                </ListItem>
                                            </Tooltip>

                                        )}
                                    </React.Fragment>
                                ))}

                            </Collapse>
                        </React.Fragment>
                    ))}
                </List>
            </Drawer>
            <Box component="main" sx={{ flexGrow: 1, p: 2, overflow: 'hidden', minHeight: '' }}>
                <LayoutMain
                    title={title}
                >
                    {children}
                </LayoutMain>
            </Box>
        </Box>
    );
};
